import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { RASHI_DETAILS } from "../content/rashis";

/**
 * Rashi film strip — twelve portrait Pattachitra frames drifting sideways.
 * Side arrows (and swipe) select a rashi; the centred frame is live colour
 * and links to /rashi/{slug}; side frames are dimmed and clickable.
 * Two "peek" clones are rendered on each side so the strip always has a
 * soft trail of frames drifting off both edges — no empty left, no jump.
 */

const AUTO_MS = 6500;
const N = RASHI_DETAILS.length;
const CLONES = 2; // peek clones on each side

// Build the rendered track: [last 2, ...all 12, first 2]
const TRACK = [
  ...RASHI_DETAILS.slice(-CLONES).map((r, k) => ({ r, realIdx: N - CLONES + k, clone: true })),
  ...RASHI_DETAILS.map((r, k) => ({ r, realIdx: k, clone: false })),
  ...RASHI_DETAILS.slice(0, CLONES).map((r, k) => ({ r, realIdx: k, clone: true })),
];

export function RashiCarousel({ testId = "rashi-carousel" }) {
  // pos is an unbounded integer; active is (pos mod N)
  const [pos, setPos] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touchX = useRef(null);

  const active = RASHI_DETAILS[((pos % N) + N) % N];

  const go = (d) => setPos((v) => v + d);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    timer.current = window.setTimeout(() => setPos((v) => v + 1), AUTO_MS);
    return () => window.clearTimeout(timer.current);
  }, [pos, paused]);

  // Silent snap so pos stays within a narrow band and DOM clones never run out
  useEffect(() => {
    if (pos >= N || pos < 0) {
      const id = window.setTimeout(() => {
        setAnimate(false);
        setPos((v) => ((v % N) + N) % N);
        // Re-enable transition on the next paint tick
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setAnimate(true))
        );
      }, 860); // just after the 850ms transform transition
      return () => window.clearTimeout(id);
    }
  }, [pos]);

  return (
    <div
      data-testid={testId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">Browse by rashi</p>
        <h3
          className="mt-3 text-[28px] leading-[1.15] tracking-[-0.005em]"
          style={{ fontFamily: "Fraunces, Georgia, serif" }}
        >
          Twelve signs, and the stone traditionally read with each
        </h3>
        <p
          className="mt-3 text-[15px] leading-[1.7] text-dim"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          The stone of a rashi is the stone of its lord, and we say so on every page. Walk the
          twelve signs with the arrows — tap the centre frame to read one in full.
        </p>
      </div>

      <div
        className="rashi-film mt-12"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}
      >
        <div
          className="rashi-film__track"
          style={{
            "--pos": pos + CLONES,
            transition: animate
              ? "transform 850ms cubic-bezier(0.16, 1, 0.3, 1)"
              : "none",
          }}
        >
          {TRACK.map((item, trackIdx) => {
            const r = item.r;
            const isActive = !item.clone && item.realIdx === (((pos % N) + N) % N);
            const inner = (
              <>
                <img
                  src={`/img/rashi/${r.slug}.webp`}
                  alt={isActive ? `${r.name} — ${r.english}, Pattachitra illustration` : ""}
                  loading="eager"
                  decoding="async"
                />
                <span className="rashi-frame__scrim" aria-hidden="true" />
                {isActive ? (
                  <span className="rashi-frame__cap">
                    <span className="rashi-frame__deva deva">{r.deva}</span>
                    <span className="rashi-frame__name">
                      {r.name} <em>· {r.english}</em>
                    </span>
                    <span className="rashi-frame__stone ui">
                      {r.stone_name} — through its lord, {r.lord_name.split(" — ")[0]}
                    </span>
                    <span className="rashi-frame__cta ui">
                      Read the rashi <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </span>
                ) : (
                  <span className="rashi-frame__ghost ui">{r.name}</span>
                )}
                <span className="rashi-frame__count num">
                  {String(item.realIdx + 1).padStart(2, "0")} / {N}
                </span>
              </>
            );

            const commonProps = {
              className: "rashi-frame",
              "data-active": isActive ? "true" : "false",
              "data-clone": item.clone ? "true" : "false",
              "aria-hidden": item.clone ? "true" : undefined,
            };

            if (isActive) {
              return (
                <Link
                  key={`t-${trackIdx}`}
                  to={`/rashi/${r.slug}`}
                  {...commonProps}
                  aria-label={`${r.name} — ${r.english}. Open detail page.`}
                  data-testid={`${testId}-frame-${r.slug}`}
                >
                  {inner}
                </Link>
              );
            }

            return (
              <button
                key={`t-${trackIdx}`}
                type="button"
                {...commonProps}
                onClick={() => {
                  // If a clone was clicked, jump to the same visual position
                  // by walking pos toward the clicked frame's real index.
                  const currentActive = ((pos % N) + N) % N;
                  let diff = item.realIdx - currentActive;
                  // Choose the shortest direction so movement mirrors the click
                  if (diff > N / 2) diff -= N;
                  if (diff < -N / 2) diff += N;
                  setPos((v) => v + diff);
                }}
                aria-label={item.clone ? undefined : `Show ${r.name}`}
                tabIndex={item.clone ? -1 : 0}
                data-testid={item.clone ? undefined : `${testId}-frame-${r.slug}`}
              >
                {inner}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="rashi-film__arrow rashi-film__arrow--l"
          onClick={() => go(-1)}
          aria-label="Previous rashi"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          className="rashi-film__arrow rashi-film__arrow--r"
          onClick={() => go(1)}
          aria-label="Next rashi"
          data-testid={`${testId}-next`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-9 flex flex-wrap items-center justify-center gap-4 text-[12px]">
        <Link
          to={`/rashi/${active.slug}`}
          className="ui inline-flex items-center gap-1.5 font-medium uppercase tracking-[0.14em] text-gold hover:underline"
          data-testid={`${testId}-primary-link`}
        >
          Open {active.name}'s page <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <span className="text-dim">or</span>
        <Link to="/shop/by-rashi" className="ui text-dim underline underline-offset-4 hover:text-ink">
          browse the shop by rashi
        </Link>
      </div>
    </div>
  );
}
